import React, { useState } from "react";
import { Item, StoreDetails, DeliveryCharge, Discount } from "../../types";
import "./ReceiptPreview.css";
import "../../styles/print.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";

interface ReceiptPreviewProps {
    items: Item[];
    onRemoveItem: (id: string) => void;
    onUpdateItem: (id: string, updates: Partial<Item>) => void;
    storeDetails?: StoreDetails;
    deliveryCharge?: DeliveryCharge;
    discount?: Discount;
}

const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({
    items,
    onRemoveItem,
    onUpdateItem,
    storeDetails = {
        name: "Catelyn Pet Shop",
        address: "Bot Tola Railgate (Sector 4), Uttara, Dhaka",
        phone: "01800-072-072",
        email: "catelynpetshop@gmail.com",
        website: "www.catelynpetshop.com",
    },
    deliveryCharge = { label: "Delivery Charge", amount: 0 },
    discount = { label: "Discount", amount: 0 },
}) => {
    const [currentDate] = useState<Date>(new Date());
    const [receiptNumber] = useState<string>(
        `R-${Math.floor(100000 + Math.random() * 900000)}`
    );

    // Calculate totals
    const subtotal = items.reduce(
        (sum, item) => sum + item.quantity * item.price,
        0
    );
    const discountedSubtotal = subtotal - discount.amount;
    const total = discountedSubtotal + deliveryCharge.amount;

    // Print function can be re-enabled when needed
    // const handlePrint = () => {
    //     window.print();
    // };

    // Always-on inline editing: update items immediately on input change
    const handleQuantityChange = (id: string, value: string) => {
        const n = Math.max(1, Math.floor(Number(value)) || 1);
        onUpdateItem(id, { quantity: n });
    };

    const handlePriceChange = (id: string, value: string) => {
        const n = Math.max(0, Number(value));
        onUpdateItem(id, { price: isNaN(n) ? 0 : n });
    };
    if (items.length === 0) {
        return (
            <div className="receipt-preview-container">
                <h2>Receipt Preview</h2>
                <div className="empty-receipt">
                    <p>No items added yet.</p>
                    <p>Use the form above to add items to the receipt.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="receipt-preview-container">
            <div className="receipt-actions">
                <h2>Receipt Preview</h2>
            </div>

            {/* Brand Header (Logo left, Titles right) */}
            <div className="receipt-brand-header">
                <div className="brand-logo">
                    <img src="/logo192.png" alt="Store Logo" />
                </div>
                <div className="brand-titles">
                    <div className="store-name">{storeDetails.name}</div>
                    <div className="store-address">{storeDetails.address}</div>
                    <div className="store-contact">{storeDetails.phone}</div>
                </div>
            </div>

            {/* Store Details (kept for screen view; hidden in print) */}
            <div className="store-details">
                <div className="store-name">{storeDetails.name}</div>
                <div className="store-address">{storeDetails.address}</div>
                <div className="store-contact">{storeDetails.phone}</div>
                {storeDetails.email && (
                    <div className="store-contact">{storeDetails.email}</div>
                )}
                {storeDetails.website && (
                    <div className="store-contact">{storeDetails.website}</div>
                )}
            </div>

            {/* Receipt Number and Date/Time - Only visible when printing */}
            <div className="receipt-header">
                <div className="receipt-number">Receipt: {receiptNumber}</div>
                <div className="receipt-datetime">
                    <span>Date: {currentDate.toLocaleDateString()}</span>
                    <span>Time: {currentDate.toLocaleTimeString()}</span>
                </div>
            </div>

            <div className="receipt-table-container">
                <table className="receipt-table">
                    <thead>
                        <tr>
                            <th className="empty-column"></th>
                            <th className="qty-column">Qty</th>
                            <th className="price-column">Price</th>
                            <th className="total-column">Total</th>
                            <th className="action-column">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item) => (
                            <React.Fragment key={item.id}>
                                {/* Item name row */}
                                <tr className={`item-name-row`}>
                                    <td colSpan={5} className="item-name">
                                        {item.name}
                                    </td>
                                </tr>
                                <tr className={`item-details-row`}>
                                    <td className="empty-cell"></td>
                                    <td>
                                        <input
                                            type="number"
                                            min="1"
                                            value={item.quantity}
                                            onChange={(e) =>
                                                handleQuantityChange(
                                                    item.id,
                                                    e.target.value
                                                )
                                            }
                                            className="receipt-input"
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={item.price}
                                            onChange={(e) =>
                                                handlePriceChange(
                                                    item.id,
                                                    e.target.value
                                                )
                                            }
                                            className="receipt-input"
                                        />
                                    </td>
                                    <td>
                                        Tk{" "}
                                        {(item.quantity * item.price).toFixed(
                                            2
                                        )}
                                    </td>
                                    <td className="action-buttons screen-only">
                                        <button
                                            className="icon-button remove-button"
                                            onClick={() =>
                                                onRemoveItem(item.id)
                                            }
                                            aria-label={`Remove ${item.name}`}
                                            title="Remove"
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                                {/* Spacer row for print only */}
                                <tr className="spacer-row print-only">
                                    <td colSpan={5}></td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Totals Section */}
            <div className="receipt-totals-wrapper">
                <div className="totals-container">
                    <div className="totals-row">
                        <span className="totals-label">Subtotal:</span>
                        <span className="totals-value">
                            Tk {subtotal.toFixed(2)}
                        </span>
                    </div>

                    {discount.amount > 0 && (
                        <div className="totals-row">
                            <span className="totals-label">
                                {discount.label}:
                            </span>
                            <span className="totals-value">
                                Tk {discount.amount.toFixed(2)}
                            </span>
                        </div>
                    )}

                    {deliveryCharge.amount > 0 && (
                        <div className="totals-row">
                            <span className="totals-label">
                                {deliveryCharge.label}:
                            </span>
                            <span className="totals-value">
                                Tk {deliveryCharge.amount.toFixed(2)}
                            </span>
                        </div>
                    )}

                    <div className="totals-row grand-total">
                        <span className="totals-label">TOTAL:</span>
                        <span className="totals-value">Tk {total.toFixed(2)}</span>
                    </div>
                </div>
            </div>

            {/* Follow Footer (two-column) - visible when printing */}
            <div className="receipt-follow-footer">
                <div className="follow-left">
                    You can follow us on facebook at fb.com/catelyn.pet.shop or by scanning the qr code
                </div>
                <div className="follow-right">
                    <img src="/cps-fb-qr-code.jpeg" alt="Facebook QR" />
                </div>
            </div>

            {/* Receipt Footer - Only visible when printing */}
            <div className="receipt-footer">
                <p>Thank you for your purchase!</p>
                <p>Visit us again soon!</p>
            </div>
        </div>
    );
};

export default ReceiptPreview;
